import { useState, useMemo, useEffect } from 'react'

import {
  getColumns,
  getColumnsCompleted,
  getColumnsInProgress,
  getColumnsPending,
} from '@/features/withdraws/table'
import { MobileBlockTraderWithdraw } from '@/features/withdraws/ui/mobile/mobileBlocks/MobileBlockTraderWithdraw'
import { useReferralWithdraws } from '../../../hooks/useReferralWithdraws'
import { TraderWithdrawsReferralListParamsStatusEnum } from 'syncra-api-client'
import { DEFAULT_TAKE } from '@/shared/constants/constants'
import { BaseTabs } from '@/shared/components'
import { OptionModel } from '@/shared/lib/types'
import { useDebounce } from '@/shared/hooks'
import type { TraderWithdrawProfile } from 'syncra-api-client'
import { ColumnDef } from '@tanstack/react-table'
import { BaseTable, BaseTableSkeleton } from 'syncra-storybook/features'

type TraderWithdrawWithId = TraderWithdrawProfile & { id: string }

// Статусы для вкладок (соответствуют API /v1/trader/withdraws/referral)
const STATUS_TABS: OptionModel[] = [
  { index: 0, label: 'Все', value: TraderWithdrawsReferralListParamsStatusEnum.Value1 },
  { index: 1, label: 'Ожидают', value: TraderWithdrawsReferralListParamsStatusEnum.Value2 },
  { index: 2, label: 'В работе', value: TraderWithdrawsReferralListParamsStatusEnum.Value3 },
  { index: 3, label: 'Оплачены', value: TraderWithdrawsReferralListParamsStatusEnum.Value4 },
  { index: 4, label: 'Ошибки', value: TraderWithdrawsReferralListParamsStatusEnum.Value5 },
]

interface ReferralWithdrawsPanelProps {
  referralId: string
}

export const ReferralWithdrawsPanel = ({ referralId }: ReferralWithdrawsPanelProps) => {
  const [selectedStatusIndex, setSelectedStatusIndex] = useState(TraderWithdrawsReferralListParamsStatusEnum.Value1) // 'Все' по умолчанию
  const [skip, setSkip] = useState(0)
  const debouncedSelectedStatusIndex = useDebounce(selectedStatusIndex, 500)

  // Сбрасываем пагинацию при смене статуса
  useEffect(() => {
    setSkip(0)
  }, [selectedStatusIndex])

  const { data, loading, error } = useReferralWithdraws({
    referralId,
    status: debouncedSelectedStatusIndex,
    skip,
    take: DEFAULT_TAKE,
  })

  const handlePageChange = (newSkip: number) => {
    setSkip(newSkip)
  }

  const itemsWithId = data.items ? data.items.filter((item: TraderWithdrawProfile): item is TraderWithdrawWithId => !!item.id) : []

  // Выбираем колонки в зависимости от статуса и убираем колонку с кнопкой действия для рефералов
  const columns = useMemo<ColumnDef<TraderWithdrawWithId, unknown>[]>(() => {
    let cols: ColumnDef<TraderWithdrawWithId, unknown>[]
    
    switch (debouncedSelectedStatusIndex) {
      case TraderWithdrawsReferralListParamsStatusEnum.Value2: // Ожидают
        cols = getColumnsPending({
          onAction: () => {}, // Для рефералов действия не нужны
          loadingIds: [],
        }) as ColumnDef<TraderWithdrawWithId, unknown>[]
        break
      case TraderWithdrawsReferralListParamsStatusEnum.Value3: // В работе
        cols = getColumnsInProgress({
          disabled: false,
          onAction: () => {}, // Для рефералов действия не нужны
        }) as ColumnDef<TraderWithdrawWithId, unknown>[]
        break
      case TraderWithdrawsReferralListParamsStatusEnum.Value4: // Оплачены
        cols = getColumnsCompleted({ includeReceipts: false, showRate: true }) as ColumnDef<TraderWithdrawWithId, unknown>[]
        break
      case TraderWithdrawsReferralListParamsStatusEnum.Value5: // Ошибки
        cols = getColumns() as ColumnDef<TraderWithdrawWithId, unknown>[]
        break
      default: // Все (Value1) или другие
        cols = getColumns() as ColumnDef<TraderWithdrawWithId, unknown>[]
    }
    
    // Убираем колонку с кнопкой действия (action) для рефералов
    return cols.filter((col) => {
      const accessorKey = 'accessorKey' in col ? col.accessorKey : undefined
      const id = 'id' in col ? col.id : undefined
      return accessorKey !== 'action' && id !== 'action'
    })
  }, [debouncedSelectedStatusIndex])

  return (
    <div className="flex flex-col gap-6">
      <BaseTabs
        tabs={STATUS_TABS}
        selectedTab={selectedStatusIndex}
        onSelect={(e) => setSelectedStatusIndex(e)}
        variant="secondary"
        direction="row"
      />

      <div className="mt-8">
        {loading ? (
          <BaseTableSkeleton />
        ) : error ? (
          <div className="p-4 text-center">
            <p className="text-red-500">Ошибка загрузки данных: {error?.message || 'Неизвестная ошибка'}</p>
          </div>
        ) : !data.items || data.items.length === 0 || itemsWithId.length === 0 ? (
          <p>Исходящих заявок нет.</p>
        ) : (
          <BaseTable<TraderWithdrawWithId, unknown>
            columns={columns}
            data={itemsWithId}
            cardClasses="border-0"
            onPageChange={handlePageChange}
            skip={data.effectiveSkip}
            totalItems={data.count}
            mobileCards={itemsWithId.map((el: TraderWithdrawWithId) => (
              <MobileBlockTraderWithdraw key={el.id} withdraw={el as any} showPayoutButtonOnMobile={false} />
            ))}
          />
        )}
      </div>
    </div>
  )
}
